import { DESKTOP_APP_IDS, DESKTOP_APP_ID_SET, DESKTOP_APP_LABELS, DESKTOP_ESSENTIAL_APP_IDS } from "./desktopAppIds"

export const DESKTOP_LAYOUT_KEY = "mark2_desktop_layout_v1"
export const DESKTOP_PREFS_KEY = "mark2_desktop_prefs"
export const DESKTOP_SCHEMA_VERSION = 12
let desktopStorageAccount = "signed-out"

const THEMES = new Set(["glass", "solid", "minimal"])
const WALLPAPERS = new Set(["aurora", "midnight", "nebula", "custom", "video"])
const ICON_STYLES = new Set(["outline", "tile", "mono"])
const BACKGROUND_FITS = new Set(["cover", "contain"])
const TASKBAR_STYLES = new Set(["full", "transparent", "segments"])
const TASKBAR_ALIGNMENTS = new Set(["left", "center", "right"])
const VISUAL_PRESETS = new Set(["minimal", "balanced", "cinematic"])
const PRODUCTIVITY_HUB_PREFS = ["sessionsEnabled", "clipboardHistoryEnabled", "quickNotesEnabled", "shelfEnabled", "routinesEnabled", "macrosEnabled"]

export const DESKTOP_DEFAULTS = {
  wallpaper: "aurora",
  customWallpaper: "",
  backgroundFit: "cover",
  backgroundDim: 0.18,
  backgroundBlur: 0,
  ambientMotion: true,
  particles: true,
  chatWindowEnabled: true,
  commandCenterEnabled: true,
  focusModeEnabled: true,
  sessionsEnabled: true,
  inboxEnabled: true,
  sidekickEnabled: true,
  agentControlEnabled: true,
  agentDropEnabled: true,
  clipboardHistoryEnabled: true,
  quickNotesEnabled: true,
  shelfEnabled: true,
  missionControlEnabled: true,
  liveActivitiesEnabled: true,
  routinesEnabled: true,
  macrosEnabled: true,
  splitWorkspacesEnabled: true,
  ruleBookEnabled: true,
  aiControlEnabled: true,
  holosFilesEnabled: true,
  globalSearchEnabled: true,
  sharedWorkspaceEnabled: true,
  resourceGovernorEnabled: true,
  permissionCenterEnabled: true,
  appStudioEnabled: true,
  codingCalendarEnabled: true,
  adaptiveDayMode: false,
  audioReactiveBackground: false,
  windowEffects: true,
  visualPreset: "balanced",
  simpleMode: false,
  focusHideTaskbar: false,
  focusMuteNotifications: true,
  performanceMode: false,
  taskbarStyle: "segments",
  taskbarAlignment: "center",
  browserUseChromeProfile: false,
  accountSyncEnabled: true,
  adaptivePerformance: true,
  restoreWindowSession: true,
  monitorSyncEnabled: true,
  maxOpenWindows: 8,
  taskbarOpacity: 0.76,
  windowOpacity: 0.96,
  windowRadius: 12,
  theme: "glass",
  autoStart: false,
  snapToGrid: true,
  iconSize: 72,
  taskbarSize: 58,
}

const finite = (value, fallback, min, max) => {
  const number = Number(value)
  return Number.isFinite(number) ? Math.min(max, Math.max(min, number)) : fallback
}

const text = (value, fallback, max = 48) =>
  typeof value === "string" && value.trim() ? value.trim().slice(0, max) : fallback

const itemId = (prefix) => `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`

export function normalizeDesktopPrefs(value = {}) {
  const source = value && typeof value === "object" ? value : {}
  return {
    wallpaper: WALLPAPERS.has(source.wallpaper) ? source.wallpaper : DESKTOP_DEFAULTS.wallpaper,
    customWallpaper: typeof source.customWallpaper === "string" ? source.customWallpaper.slice(0, 2048) : "",
    backgroundFit: BACKGROUND_FITS.has(source.backgroundFit) ? source.backgroundFit : DESKTOP_DEFAULTS.backgroundFit,
    backgroundDim: finite(source.backgroundDim, DESKTOP_DEFAULTS.backgroundDim, 0, 0.8),
    backgroundBlur: finite(source.backgroundBlur, DESKTOP_DEFAULTS.backgroundBlur, 0, 24),
    ambientMotion: typeof source.ambientMotion === "boolean" ? source.ambientMotion : DESKTOP_DEFAULTS.ambientMotion,
    particles: typeof source.particles === "boolean" ? source.particles : DESKTOP_DEFAULTS.particles,
    chatWindowEnabled: typeof source.chatWindowEnabled === "boolean" ? source.chatWindowEnabled : DESKTOP_DEFAULTS.chatWindowEnabled,
    commandCenterEnabled: typeof source.commandCenterEnabled === "boolean" ? source.commandCenterEnabled : DESKTOP_DEFAULTS.commandCenterEnabled,
    focusModeEnabled: typeof source.focusModeEnabled === "boolean" ? source.focusModeEnabled : DESKTOP_DEFAULTS.focusModeEnabled,
    sessionsEnabled: typeof source.sessionsEnabled === "boolean" ? source.sessionsEnabled : DESKTOP_DEFAULTS.sessionsEnabled,
    inboxEnabled: typeof source.inboxEnabled === "boolean" ? source.inboxEnabled : DESKTOP_DEFAULTS.inboxEnabled,
    sidekickEnabled: typeof source.sidekickEnabled === "boolean" ? source.sidekickEnabled : DESKTOP_DEFAULTS.sidekickEnabled,
    agentControlEnabled: typeof source.agentControlEnabled === "boolean" ? source.agentControlEnabled : DESKTOP_DEFAULTS.agentControlEnabled,
    agentDropEnabled: typeof source.agentDropEnabled === "boolean" ? source.agentDropEnabled : DESKTOP_DEFAULTS.agentDropEnabled,
    clipboardHistoryEnabled: typeof source.clipboardHistoryEnabled === "boolean" ? source.clipboardHistoryEnabled : DESKTOP_DEFAULTS.clipboardHistoryEnabled,
    quickNotesEnabled: typeof source.quickNotesEnabled === "boolean" ? source.quickNotesEnabled : DESKTOP_DEFAULTS.quickNotesEnabled,
    shelfEnabled: typeof source.shelfEnabled === "boolean" ? source.shelfEnabled : DESKTOP_DEFAULTS.shelfEnabled,
    missionControlEnabled: typeof source.missionControlEnabled === "boolean" ? source.missionControlEnabled : DESKTOP_DEFAULTS.missionControlEnabled,
    liveActivitiesEnabled: typeof source.liveActivitiesEnabled === "boolean" ? source.liveActivitiesEnabled : DESKTOP_DEFAULTS.liveActivitiesEnabled,
    routinesEnabled: typeof source.routinesEnabled === "boolean" ? source.routinesEnabled : DESKTOP_DEFAULTS.routinesEnabled,
    macrosEnabled: typeof source.macrosEnabled === "boolean" ? source.macrosEnabled : DESKTOP_DEFAULTS.macrosEnabled,
    splitWorkspacesEnabled: typeof source.splitWorkspacesEnabled === "boolean" ? source.splitWorkspacesEnabled : DESKTOP_DEFAULTS.splitWorkspacesEnabled,
    ruleBookEnabled: typeof source.ruleBookEnabled === "boolean" ? source.ruleBookEnabled : DESKTOP_DEFAULTS.ruleBookEnabled,
    aiControlEnabled: typeof source.aiControlEnabled === "boolean" ? source.aiControlEnabled : DESKTOP_DEFAULTS.aiControlEnabled,
    holosFilesEnabled: typeof source.holosFilesEnabled === "boolean" ? source.holosFilesEnabled : DESKTOP_DEFAULTS.holosFilesEnabled,
    globalSearchEnabled: typeof source.globalSearchEnabled === "boolean" ? source.globalSearchEnabled : DESKTOP_DEFAULTS.globalSearchEnabled,
    sharedWorkspaceEnabled: typeof source.sharedWorkspaceEnabled === "boolean" ? source.sharedWorkspaceEnabled : DESKTOP_DEFAULTS.sharedWorkspaceEnabled,
    resourceGovernorEnabled: typeof source.resourceGovernorEnabled === "boolean" ? source.resourceGovernorEnabled : DESKTOP_DEFAULTS.resourceGovernorEnabled,
    permissionCenterEnabled: typeof source.permissionCenterEnabled === "boolean" ? source.permissionCenterEnabled : DESKTOP_DEFAULTS.permissionCenterEnabled,
    appStudioEnabled: typeof source.appStudioEnabled === "boolean" ? source.appStudioEnabled : DESKTOP_DEFAULTS.appStudioEnabled,
    codingCalendarEnabled: typeof source.codingCalendarEnabled === "boolean" ? source.codingCalendarEnabled : DESKTOP_DEFAULTS.codingCalendarEnabled,
    adaptiveDayMode: typeof source.adaptiveDayMode === "boolean" ? source.adaptiveDayMode : DESKTOP_DEFAULTS.adaptiveDayMode,
    audioReactiveBackground: typeof source.audioReactiveBackground === "boolean" ? source.audioReactiveBackground : DESKTOP_DEFAULTS.audioReactiveBackground,
    windowEffects: typeof source.windowEffects === "boolean" ? source.windowEffects : DESKTOP_DEFAULTS.windowEffects,
    visualPreset: VISUAL_PRESETS.has(source.visualPreset) ? source.visualPreset : DESKTOP_DEFAULTS.visualPreset,
    simpleMode: typeof source.simpleMode === "boolean" ? source.simpleMode : DESKTOP_DEFAULTS.simpleMode,
    focusHideTaskbar: typeof source.focusHideTaskbar === "boolean" ? source.focusHideTaskbar : DESKTOP_DEFAULTS.focusHideTaskbar,
    focusMuteNotifications: typeof source.focusMuteNotifications === "boolean" ? source.focusMuteNotifications : DESKTOP_DEFAULTS.focusMuteNotifications,
    performanceMode: typeof source.performanceMode === "boolean" ? source.performanceMode : DESKTOP_DEFAULTS.performanceMode,
    taskbarStyle: TASKBAR_STYLES.has(source.taskbarStyle) ? source.taskbarStyle : DESKTOP_DEFAULTS.taskbarStyle,
    taskbarAlignment: TASKBAR_ALIGNMENTS.has(source.taskbarAlignment) ? source.taskbarAlignment : DESKTOP_DEFAULTS.taskbarAlignment,
    browserUseChromeProfile: typeof source.browserUseChromeProfile === "boolean" ? source.browserUseChromeProfile : DESKTOP_DEFAULTS.browserUseChromeProfile,
    accountSyncEnabled: typeof source.accountSyncEnabled === "boolean" ? source.accountSyncEnabled : DESKTOP_DEFAULTS.accountSyncEnabled,
    adaptivePerformance: typeof source.adaptivePerformance === "boolean" ? source.adaptivePerformance : DESKTOP_DEFAULTS.adaptivePerformance,
    restoreWindowSession: typeof source.restoreWindowSession === "boolean" ? source.restoreWindowSession : DESKTOP_DEFAULTS.restoreWindowSession,
    monitorSyncEnabled: typeof source.monitorSyncEnabled === "boolean" ? source.monitorSyncEnabled : DESKTOP_DEFAULTS.monitorSyncEnabled,
    maxOpenWindows: finite(source.maxOpenWindows, DESKTOP_DEFAULTS.maxOpenWindows, 4, 12),
    taskbarOpacity: finite(source.taskbarOpacity, DESKTOP_DEFAULTS.taskbarOpacity, 0.2, 1),
    windowOpacity: finite(source.windowOpacity, DESKTOP_DEFAULTS.windowOpacity, 0.7, 1),
    windowRadius: finite(source.windowRadius, DESKTOP_DEFAULTS.windowRadius, 0, 24),
    theme: THEMES.has(source.theme) ? source.theme : DESKTOP_DEFAULTS.theme,
    autoStart: typeof source.autoStart === "boolean" ? source.autoStart : DESKTOP_DEFAULTS.autoStart,
    snapToGrid: typeof source.snapToGrid === "boolean" ? source.snapToGrid : DESKTOP_DEFAULTS.snapToGrid,
    iconSize: finite(source.iconSize, DESKTOP_DEFAULTS.iconSize, 56, 104),
    taskbarSize: finite(source.taskbarSize, DESKTOP_DEFAULTS.taskbarSize, 48, 76),
  }
}

export function createDesktop(name = "Desktop", index = 0) {
  return {
    id: itemId("desktop"),
    name: text(name, `Desktop ${index + 1}`),
    items: [],
    widgets: [],
    appearance: {
      wallpaper: null,
      customWallpaper: "",
      backgroundFit: null,
      backgroundDim: null,
      backgroundBlur: null,
      ambientMotion: null,
      particles: null,
      theme: null,
      iconStyle: "outline",
    },
  }
}

function appItem(appId, label, x, y) {
  return { id: itemId("app"), kind: "app", appId, label, x, y, simpleHidden: !DESKTOP_ESSENTIAL_APP_IDS.has(appId) }
}

export function createDefaultDesktopState() {
  const work = createDesktop("Arbeit", 0)
  work.items = DESKTOP_APP_IDS.map((appId, index) =>
    appItem(appId, DESKTOP_APP_LABELS[appId], 28 + (index % 8) * 100, 78 + Math.floor(index / 8) * 102)
  )
  const personal = createDesktop("Persönlich", 1)
  personal.items = [
    appItem("mail", "Mail", 28, 30),
    appItem("reminder", "Reminder", 128, 30),
    appItem("chill", "Chill", 228, 30),
    appItem("hut", "Hut & Heroes", 328, 30),
  ]
  return {
    schemaVersion: DESKTOP_SCHEMA_VERSION,
    activeDesktopId: work.id,
    desktops: [work, personal],
    pinned: ["assistants", "browser", "terminal", "ai-control"],
    trash: [],
  }
}

function normalizeChild(value) {
  if (!value || typeof value !== "object" || !DESKTOP_APP_ID_SET.has(value.appId)) return null
  return {
    id: text(value.id, itemId("app"), 80),
    appId: value.appId,
    label: text(value.label, value.appId),
  }
}

function normalizeItem(value, index) {
  if (!value || typeof value !== "object") return null
  const kind = value.kind === "folder" ? "folder" : "app"
  if (kind === "app" && !DESKTOP_APP_ID_SET.has(value.appId)) return null
  const childIds = new Set()
  const children = kind === "folder" && Array.isArray(value.children)
    ? value.children.slice(0, 40).map(normalizeChild).filter(Boolean).map((child) => {
      let id = child.id
      while (childIds.has(id)) id = itemId("app")
      childIds.add(id)
      return { ...child, id }
    })
    : []
  return {
    id: text(value.id, itemId(kind), 80),
    kind,
    ...(kind === "app" ? { appId: value.appId } : { children }),
    label: text(value.label, kind === "folder" ? "Neuer Ordner" : value.appId),
    x: finite(value.x, 28 + (index % 7) * 100, 0, 5000),
    y: finite(value.y, 30 + Math.floor(index / 7) * 102, 0, 5000),
    ...(kind === "app" ? { simpleHidden: typeof value.simpleHidden === "boolean" ? value.simpleHidden : !DESKTOP_ESSENTIAL_APP_IDS.has(value.appId) } : {}),
  }
}

function normalizeWidget(value, index) {
  if (!value || typeof value !== "object" || typeof value.type !== "string") return null
  return {
    id: text(value.id, itemId("widget"), 100),
    type: value.type.slice(0, 64),
    x: finite(value.x, 24 + index * 28, 0, 5000),
    y: finite(value.y, 230 + index * 28, 0, 5000),
    w: finite(value.w, 300, 180, 1200),
    h: finite(value.h, 200, 100, 900),
    ...(typeof value.title === "string" ? { title: value.title.slice(0, 80) } : {}),
    ...(/^#[0-9a-f]{6}$/i.test(value.accent) ? { accent: value.accent } : {}),
    ...(Number.isFinite(Number(value.refreshMs)) ? { refreshMs: finite(value.refreshMs, 4000, 2000, 60000) } : {}),
    ...(typeof value.collapsed === "boolean" ? { collapsed: value.collapsed } : {}),
    ...(typeof value.notifications === "boolean" ? { notifications: value.notifications } : {}),
  }
}

function normalizeDesktop(value, index) {
  if (!value || typeof value !== "object") return null
  const usedIds = new Set()
  const unique = (entry, prefix) => {
    let id = entry.id
    while (usedIds.has(id)) id = itemId(prefix)
    usedIds.add(id)
    return { ...entry, id }
  }
  return {
    id: text(value.id, itemId("desktop"), 80),
    name: text(value.name, `Desktop ${index + 1}`),
    items: Array.isArray(value.items) ? value.items.slice(0, 80).map(normalizeItem).filter(Boolean).map((entry) => unique(entry, entry.kind)) : [],
    widgets: Array.isArray(value.widgets) ? value.widgets.slice(0, 30).map(normalizeWidget).filter(Boolean).map((entry) => unique(entry, "widget")) : [],
    appearance: {
      wallpaper: WALLPAPERS.has(value.appearance?.wallpaper) ? value.appearance.wallpaper : null,
      customWallpaper: typeof value.appearance?.customWallpaper === "string" ? value.appearance.customWallpaper.slice(0, 2048) : "",
      backgroundFit: BACKGROUND_FITS.has(value.appearance?.backgroundFit) ? value.appearance.backgroundFit : null,
      backgroundDim: value.appearance?.backgroundDim == null ? null : finite(value.appearance.backgroundDim, null, 0, 0.8),
      backgroundBlur: value.appearance?.backgroundBlur == null ? null : finite(value.appearance.backgroundBlur, null, 0, 24),
      ambientMotion: typeof value.appearance?.ambientMotion === "boolean" ? value.appearance.ambientMotion : null,
      particles: typeof value.appearance?.particles === "boolean" ? value.appearance.particles : null,
      theme: THEMES.has(value.appearance?.theme) ? value.appearance.theme : null,
      iconStyle: ICON_STYLES.has(value.appearance?.iconStyle) ? value.appearance.iconStyle : "outline",
    },
  }
}

export function normalizeDesktopState(value) {
  const fallback = createDefaultDesktopState()
  if (!value || typeof value !== "object") return fallback
  const usedDesktopIds = new Set()
  let desktops = Array.isArray(value.desktops) ? value.desktops.slice(0, 8).map(normalizeDesktop).filter(Boolean).map((desktop) => {
    let id = desktop.id
    while (usedDesktopIds.has(id)) id = itemId("desktop")
    usedDesktopIds.add(id)
    return { ...desktop, id }
  }) : []
  if (!desktops.length) return fallback
  const sourceVersion = Number(value.schemaVersion ?? 1)
  if (sourceVersion < DESKTOP_SCHEMA_VERSION) {
    const first = desktops[0]
    const existing = new Set()
    desktops.forEach((desktop) => desktop.items.forEach((item) => {
      if (item.kind === "app") existing.add(item.appId)
      else item.children.forEach((child) => existing.add(child.appId))
    }))
    const migrationApps = sourceVersion < 2
      ? DESKTOP_APP_IDS
      : [
        ...(sourceVersion < 3 ? ["files"] : []),
        ...(sourceVersion < 5 ? ["chat"] : []),
        ...(sourceVersion < 6 ? ["productivity", "inbox", "agent-control", "mission-control"] : []),
        ...(sourceVersion < 7 ? ["deploy"] : []),
        ...(sourceVersion < 9 ? ["rules"] : []),
        ...(sourceVersion < 10 ? ["workspaces", "ai-control", "global-search", "shared-workspace", "resource-governor", "permissions", "app-studio"] : []),
        ...(sourceVersion < 12 ? ["coding-calendar"] : []),
      ]
    const additions = migrationApps.filter((appId) => !existing.has(appId)).map((appId, index) => ({
      id: itemId("app"),
      kind: "app",
      appId,
      label: DESKTOP_APP_LABELS[appId],
      x: 28 + ((first.items.length + index) % 8) * 100,
      y: 78 + Math.floor((first.items.length + index) / 8) * 102,
    }))
    desktops = [{ ...first, items: [...first.items, ...additions].slice(0, 80) }, ...desktops.slice(1)]
  }
  const activeDesktopId = desktops.some((desktop) => desktop.id === value.activeDesktopId) ? value.activeDesktopId : desktops[0].id
  let pinned = Array.isArray(value.pinned)
    ? [...new Set(value.pinned.filter((id) => DESKTOP_APP_ID_SET.has(id)))].slice(0, 12)
    : fallback.pinned
  if (sourceVersion < 2 && !pinned.includes("browser")) {
    pinned = [...pinned.slice(0, 11), "browser"]
  }
  if (sourceVersion < 11 && !pinned.includes("ai-control")) {
    pinned = [...pinned.slice(0, 11), "ai-control"]
  }
  const trash = Array.isArray(value.trash)
    ? value.trash.slice(0, 50).map((entry) => {
      const item = normalizeItem(entry?.item)
      if (!item) return null
      const requestedDesktopId = typeof entry?.desktopId === "string" ? entry.desktopId.slice(0, 80) : activeDesktopId
      return {
        id: text(entry?.id, itemId("trash"), 80),
        deletedAt: text(entry?.deletedAt, new Date().toISOString(), 40),
        desktopId: desktops.some((desktop) => desktop.id === requestedDesktopId) ? requestedDesktopId : activeDesktopId,
        item,
      }
    }).filter(Boolean)
    : []
  return { schemaVersion: DESKTOP_SCHEMA_VERSION, activeDesktopId, desktops, pinned, trash }
}

const scopedKey = (key, accountId = desktopStorageAccount) =>
  `${key}:${String(accountId || "signed-out").replace(/[^a-zA-Z0-9_.-]/g, "_").slice(0, 96)}`

export function setDesktopStorageAccount(accountId) {
  desktopStorageAccount = accountId ? String(accountId) : "signed-out"
}

export function loadDesktopState(accountId = desktopStorageAccount) {
  try {
    return normalizeDesktopState(JSON.parse(localStorage.getItem(scopedKey(DESKTOP_LAYOUT_KEY, accountId)) ?? "null"))
  } catch {
    return createDefaultDesktopState()
  }
}

export function saveDesktopState(state, accountId = desktopStorageAccount) {
  try {
    localStorage.setItem(scopedKey(DESKTOP_LAYOUT_KEY, accountId), JSON.stringify(normalizeDesktopState(state)))
  } catch { /* IndexedDB/provider sync can remain available */ }
}

export function loadDesktopPrefs(accountId = desktopStorageAccount) {
  try {
    return normalizeDesktopPrefs(JSON.parse(localStorage.getItem(scopedKey(DESKTOP_PREFS_KEY, accountId)) ?? "{}"))
  } catch {
    return { ...DESKTOP_DEFAULTS }
  }
}

export function saveDesktopPrefs(prefs, accountId = desktopStorageAccount) {
  try {
    localStorage.setItem(scopedKey(DESKTOP_PREFS_KEY, accountId), JSON.stringify(normalizeDesktopPrefs(prefs)))
  } catch { /* IndexedDB/provider sync can remain available */ }
}

export function resolveDesktopPrefs(prefs, appearance = {}) {
  return {
    ...prefs,
    ...(appearance.wallpaper ? { wallpaper: appearance.wallpaper, customWallpaper: appearance.customWallpaper } : {}),
    ...(appearance.backgroundFit ? { backgroundFit: appearance.backgroundFit } : {}),
    ...(appearance.backgroundDim != null ? { backgroundDim: appearance.backgroundDim } : {}),
    ...(appearance.backgroundBlur != null ? { backgroundBlur: appearance.backgroundBlur } : {}),
    ...(appearance.ambientMotion != null ? { ambientMotion: appearance.ambientMotion } : {}),
    ...(appearance.particles != null ? { particles: appearance.particles } : {}),
    ...(appearance.theme ? { theme: appearance.theme } : {}),
  }
}

export function isProductivityHubEnabled(prefs) {
  return PRODUCTIVITY_HUB_PREFS.some((key) => prefs[key] !== false)
}

export function reorderPinnedApps(state, sourceId, targetId) {
  const pinned = state.pinned.filter((id) => id !== sourceId)
  const targetIndex = pinned.indexOf(targetId)
  pinned.splice(targetIndex < 0 ? pinned.length : targetIndex, 0, sourceId)
  return { ...state, pinned: pinned.slice(0, 12) }
}

export function parseDesktopLayout(document) {
  if (!document || ![1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, DESKTOP_SCHEMA_VERSION].includes(document.schemaVersion) || !document.layout) {
    throw new Error("Diese Datei ist kein gültiges Desktop-Layout.")
  }
  return normalizeDesktopState(document.layout)
}

export function exportDesktopLayout(state) {
  return {
    schemaVersion: DESKTOP_SCHEMA_VERSION,
    exportedAt: new Date().toISOString(),
    layout: normalizeDesktopState(state),
  }
}

export function newDesktopItemId(kind) {
  return itemId(kind)
}
